import type {
  ContinuityObjectState,
  PhysicalObjectType,
} from '../types';

export interface ContinuityObjectIdentity {
  objectId: string;
  projectId: string;
  objectType: PhysicalObjectType;
  canonicalName: string;
  aliases: string[];
  firstSeenChapter: number;
  sourceCreationRecordId: string;
}

export interface ContinuityObjectMutableState {
  ownerCharacterIds: string[];
  currentHolderCharacterId?: string;
  currentLocationId?: string;
  currentLocationDescription?: string;
  operationalStatus: ContinuityObjectState['operationalStatus'];
  mobilityStatus?: ContinuityObjectState['mobilityStatus'];
  lastSeenChapter: number;
  lastChangedChapter: number;
}

export interface EntityResolutionResult {
  objectId: string;
  identity: ContinuityObjectIdentity;
  ambiguous: boolean;
  ambiguousMatches: string[];
  warnings: string[];
}

function slugify(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 48);
}

export function buildStableObjectId(params: {
  projectId?: string;
  objectType: PhysicalObjectType;
  canonicalName: string;
  firstSeenChapter: number;
  sourceCreationRecordId: string;
}): string {
  const proj = slugify(params.projectId || 'project').slice(0, 12);
  const type = slugify(params.objectType);
  const name = slugify(params.canonicalName);
  const record = slugify(params.sourceCreationRecordId).slice(0, 20);
  return `obj_${proj}_${type}_${name}_ch${params.firstSeenChapter}_${record}`.slice(0, 120);
}

function namesMatch(a: string, b: string): boolean {
  const left = a.toLowerCase();
  const right = b.toLowerCase();
  return left === right || left.includes(right) || right.includes(left);
}

function identityMatchesRecord(
  object: ContinuityObjectState,
  canonicalName: string,
  objectType: PhysicalObjectType,
): boolean {
  if (object.objectType !== objectType) return false;
  if (namesMatch(object.canonicalName, canonicalName)) return true;
  return object.aliases.some((alias) => namesMatch(alias, canonicalName));
}

/** System-issued IDs follow the buildStableObjectId slug pattern. */
export function isSystemIssuedObjectId(objectId: string): boolean {
  return /^obj_[a-z0-9_]+_ch\d+_[a-z0-9_]+$/i.test(objectId);
}

function scoreObjectMatch(
  object: ContinuityObjectState,
  canonicalName: string,
  objectType: PhysicalObjectType,
  ownerCharacterIds: string[],
): number {
  let score = 0;
  if (object.objectType !== objectType) return 0;
  if (namesMatch(object.canonicalName, canonicalName)) score += 3;
  if (object.aliases.some((alias) => namesMatch(alias, canonicalName))) score += 2;
  if (
    ownerCharacterIds.length &&
    object.ownerCharacterIds.some((owner) => ownerCharacterIds.includes(owner))
  ) {
    score += 1;
  }
  if (object.currentHolderCharacterId && ownerCharacterIds.includes(object.currentHolderCharacterId)) {
    score += 1;
  }
  return score;
}

export function resolveContinuityObjectIdentity(params: {
  projectId?: string;
  chapterNumber: number;
  index: number;
  canonicalName: string;
  objectType: PhysicalObjectType;
  aliases?: string[];
  ownerCharacterIds?: string[];
  suppliedObjectId?: string;
  existingObjects?: ContinuityObjectState[];
}): EntityResolutionResult {
  const warnings: string[] = [];
  const existingObjects = params.existingObjects || [];
  const ownerCharacterIds = params.aliases ? params.ownerCharacterIds || [] : params.ownerCharacterIds || [];
  const sourceCreationRecordId = `ch${params.chapterNumber}_rec${params.index + 1}`;

  if (params.suppliedObjectId) {
    const supplied = existingObjects.find((object) => object.objectId === params.suppliedObjectId);
    if (supplied) {
      if (identityMatchesRecord(supplied, params.canonicalName, params.objectType)) {
        return {
          objectId: supplied.objectId,
          identity: {
            objectId: supplied.objectId,
            projectId: params.projectId || 'project',
            objectType: supplied.objectType,
            canonicalName: supplied.canonicalName,
            aliases: supplied.aliases,
            firstSeenChapter: supplied.lastSeenChapter,
            sourceCreationRecordId: supplied.sourceChapterId || sourceCreationRecordId,
          },
          ambiguous: false,
          ambiguousMatches: [],
          warnings,
        };
      }
      warnings.push('SUPPLIED_ID_CONFLICT');
    } else if (isSystemIssuedObjectId(params.suppliedObjectId)) {
      return {
        objectId: params.suppliedObjectId,
        identity: {
          objectId: params.suppliedObjectId,
          projectId: params.projectId || 'project',
          objectType: params.objectType,
          canonicalName: params.canonicalName,
          aliases: params.aliases || [],
          firstSeenChapter: params.chapterNumber,
          sourceCreationRecordId,
        },
        ambiguous: false,
        ambiguousMatches: [],
        warnings,
      };
    } else {
      warnings.push('UNKNOWN_MODEL_ID_IGNORED');
    }
  }

  const scored = existingObjects
    .map((object) => ({
      object,
      score: scoreObjectMatch(object, params.canonicalName, params.objectType, ownerCharacterIds),
    }))
    .filter((entry) => entry.score > 0)
    .sort((a, b) => b.score - a.score);

  const topScore = scored[0]?.score || 0;
  const topMatches = scored.filter((entry) => entry.score === topScore && topScore > 0);

  if (topMatches.length > 1) {
    warnings.push('ENTITY_RESOLUTION_AMBIGUOUS');
    return {
      objectId: buildStableObjectId({
        projectId: params.projectId,
        objectType: params.objectType,
        canonicalName: params.canonicalName,
        firstSeenChapter: params.chapterNumber,
        sourceCreationRecordId,
      }),
      identity: {
        objectId: buildStableObjectId({
          projectId: params.projectId,
          objectType: params.objectType,
          canonicalName: params.canonicalName,
          firstSeenChapter: params.chapterNumber,
          sourceCreationRecordId,
        }),
        projectId: params.projectId || 'project',
        objectType: params.objectType,
        canonicalName: params.canonicalName,
        aliases: params.aliases || [],
        firstSeenChapter: params.chapterNumber,
        sourceCreationRecordId,
      },
      ambiguous: true,
      ambiguousMatches: topMatches.map((entry) => entry.object.objectId),
      warnings,
    };
  }

  if (topMatches.length === 1) {
    const object = topMatches[0]!.object;
    return {
      objectId: object.objectId,
      identity: {
        objectId: object.objectId,
        projectId: params.projectId || 'project',
        objectType: object.objectType,
        canonicalName: object.canonicalName,
        aliases: object.aliases,
        firstSeenChapter: object.lastSeenChapter,
        sourceCreationRecordId: object.sourceChapterId || sourceCreationRecordId,
      },
      ambiguous: false,
      ambiguousMatches: [],
      warnings,
    };
  }

  const objectId = buildStableObjectId({
    projectId: params.projectId,
    objectType: params.objectType,
    canonicalName: params.canonicalName,
    firstSeenChapter: params.chapterNumber,
    sourceCreationRecordId,
  });

  return {
    objectId,
    identity: {
      objectId,
      projectId: params.projectId || 'project',
      objectType: params.objectType,
      canonicalName: params.canonicalName,
      aliases: params.aliases || [],
      firstSeenChapter: params.chapterNumber,
      sourceCreationRecordId,
    },
    ambiguous: false,
    ambiguousMatches: [],
    warnings,
  };
}

export function splitContinuityObjectState(object: ContinuityObjectState, projectId?: string): {
  identity: ContinuityObjectIdentity;
  mutable: ContinuityObjectMutableState;
} {
  return {
    identity: {
      objectId: object.objectId,
      projectId: projectId || 'project',
      objectType: object.objectType,
      canonicalName: object.canonicalName,
      aliases: object.aliases,
      firstSeenChapter: object.lastSeenChapter,
      sourceCreationRecordId: object.sourceChapterId,
    },
    mutable: {
      ownerCharacterIds: object.ownerCharacterIds,
      currentHolderCharacterId: object.currentHolderCharacterId,
      currentLocationId: object.currentLocationId,
      currentLocationDescription: object.currentLocationDescription,
      operationalStatus: object.operationalStatus,
      mobilityStatus: object.mobilityStatus,
      lastSeenChapter: object.lastSeenChapter,
      lastChangedChapter: object.lastChangedChapter,
    },
  };
}
